import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import "./commentpopup.css";

const CommentPopup = ({ onClose, chapterId, lessonId, questionIndex, onNewComment }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [activeTab, setActiveTab] = useState("popular");
  const inputRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // 🔹 1. ÎNCARCĂ COMENTARIILE DIN BACKEND
  useEffect(() => {
    fetch(`${apiUrl}/comments/${chapterId}/${lessonId}/${questionIndex}`, {
      credentials: "include", // trimite cookie JWT
    })
      .then((res) => {
        if (res.status === 401) return []; // dacă userul nu e logat
        return res.json();
      })
      .then((data) => setComments(data))
      .catch((err) => console.error("Eroare încărcare comentarii:", err));
  }, [chapterId, lessonId, questionIndex, apiUrl]);

  // 🔹 2. TRIMITE UN COMMENT LA BACKEND
  const handlePost = async () => {
    if (!newComment.trim()) return;

    const body = {
      chapter_id: chapterId,
      lesson_id: lessonId,
      question_index: questionIndex,
      comment_text: newComment,
    };

    const res = await fetch(`${apiUrl}/comments`, {
      method: "POST",
      credentials: "include", // trimite cookie JWT
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (res.status === 401) {
      alert("Trebuie să fii autentificat pentru a posta comentarii.");
      return;
    }

    const saved = await res.json();

    // comentariul nou apare instant
    setComments((prev) => [saved, ...prev]);

    // trimite notificare către FeedbackPopup pentru a actualiza iconița
    if (onNewComment) onNewComment();

    setNewComment("");
  };

  const getAvatarInitial = (username) => {
    if (!username) return "U";
    return username.trim().charAt(0).toUpperCase();
  };

  const getPopularityScore = (comment) => {
    return Number(
      comment.likes_count ??
      comment.upvotes ??
      comment.votes ??
      comment.replies_count ??
      0
    );
  };

  const formatRelativeTime = (createdAt) => {
    if (!createdAt) return "";

    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return "";

    const diffMs = Date.now() - createdDate.getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const month = 30 * day;
    const year = 365 * day;

    if (diffMs < minute) return "just now";
    if (diffMs < hour) {
      const value = Math.floor(diffMs / minute);
      return `${value} minute${value > 1 ? "s" : ""}`;
    }
    if (diffMs < day) {
      const value = Math.floor(diffMs / hour);
      return `${value} hour${value > 1 ? "s" : ""}`;
    }
    if (diffMs < week) {
      const value = Math.floor(diffMs / day);
      return `${value} day${value > 1 ? "s" : ""}`;
    }
    if (diffMs < month) {
      const value = Math.floor(diffMs / week);
      return `${value} week${value > 1 ? "s" : ""}`;
    }
    if (diffMs < year) {
      const value = Math.floor(diffMs / month);
      return `${value} month${value > 1 ? "s" : ""}`;
    }
    const value = Math.floor(diffMs / year);
    return `${value} year${value > 1 ? "s" : ""}`;
  };

  const handleReply = (username) => {
    const safeUsername = username || "Utilizator";
    setNewComment(`@${safeUsername} `);
    inputRef.current?.focus();
  };

  const sortedComments = [...comments].sort((first, second) => {
    const firstDate = new Date(first.created_at).getTime() || 0;
    const secondDate = new Date(second.created_at).getTime() || 0;

    if (activeTab === "recent") {
      return secondDate - firstDate;
    }

    const firstPopularity = getPopularityScore(first);
    const secondPopularity = getPopularityScore(second);
    if (secondPopularity !== firstPopularity) {
      return secondPopularity - firstPopularity;
    }

    return secondDate - firstDate;
  });

  return (
    <div className="comment-popup-overlay">
      <div className="comment-popup">

        {/* Buton închidere */}
        <button className="close-popup-btn" onClick={onClose}>
          <FontAwesomeIcon icon={faXmark} size="lg" />
        </button>

        <div className="comment-top-row">
          <button className="comment-hide-btn" type="button" onClick={onClose}>
            Hide <span className="comment-hide-arrow">›</span>
          </button>
          <span className="comment-total">{comments.length} Comments</span>
        </div>

        <div className="comment-title-row">
          <h2 className="comment-header">Comentarii</h2>
        </div>

        {/* Input + Post */}
        <div className="comment-input-row">
          <textarea
            placeholder="Scrie un comentariu..."
            className="comment-input"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={2}
            ref={inputRef}
          />
          <button className="comment-post-btn" onClick={handlePost}>
            Post
          </button>
        </div>

        <div className="comment-tabs" role="tablist" aria-label="Sortare comentarii">
          <button
            className={`comment-tab ${activeTab === "popular" ? "active" : ""}`}
            onClick={() => setActiveTab("popular")}
            type="button"
          >
            Popular
          </button>
          <button
            className={`comment-tab ${activeTab === "recent" ? "active" : ""}`}
            onClick={() => setActiveTab("recent")}
            type="button"
          >
            Recent
          </button>
        </div>

        {/* Listă comentarii */}
        <div className="comment-list">
          {comments.length === 0 && (
            <p className="no-comments">Fii primul care comentează! ✍️</p>
          )}

          {sortedComments.map((c) => (
            <div key={c.id} className="comment-item">
              <div className="comment-avatar">{getAvatarInitial(c.username)}</div>
              <div className="comment-body">
                <div className="comment-user-row">
                  <b className="comment-username">{c.username || "Utilizator"}</b>
                  <span className="comment-time">{formatRelativeTime(c.created_at)}</span>
                </div>

                <p className="comment-text">{c.comment_text}</p>

                <div className="comment-meta-row">
                  <span className="comment-votes">↑ {getPopularityScore(c)}</span>
                  <button
                    className="comment-reply"
                    type="button"
                    onClick={() => handleReply(c.username)}
                  >
                    Răspunde
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default CommentPopup;
